import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CompletionBar from '../CompletionBar';
import { champsManquants } from '../reglesEtapes';
import ChampsManquants from '../ChampsManquants';
import { getPriceLabel } from '../../../constants/operationLabels';
import BarreEtape from '../BarreEtape';

const Step3_KeyInfo = ({ data, updateData, nextStep, prevStep }) => {
  const [adresseSearch, setAdresseSearch] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState([]);
  const [loadingAdresse, setLoadingAdresse] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [generatingDetailedDesc, setGeneratingDetailedDesc] = useState(false);

  const generateTitleAI = async () => {
    if (generatingTitle) return;
    setGeneratingTitle(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/properties/generate-title`,
        {
          operation: data.operation,
          objet: data.objet,
          profil: data.profil,
          sous_profil: data.sous_profil,
          ville: data.ville || data.adresse_libre || data.commune_rattachement,
          prix: data.prix || data.prix_cession || data.loyer_mensuel,
          surface: data.superficie_totale || data.surface_totale,
          enseigne: data.enseigne,
        },
        { headers },
      );
      if (resp.data?.title) {
        updateData({ titre_annonce: resp.data.title });
      }
    } catch (e) {
      alert("Erreur génération titre IA : " + (e.response?.data?.detail || e.message));
    } finally {
      setGeneratingTitle(false);
    }
  };

  const API = process.env.REACT_APP_BACKEND_URL;
  const isPrixOperation = data.operation === 'cession' || data.operation === 'vente_immo';
  const isLocationDroitEntree = data.operation === 'location_droit_entree';
  // lot BI §7a — une cession de DROIT AU BAIL demande deux montants, comme la location avec
  // pas-de-porte : le prix du droit au bail ET le loyer (sans lequel SeLoger refuse l'annonce).
  const isCessionDroitAuBail = data.operation === 'cession' && data.objet === 'droit_au_bail';
  const deuxMontants = isLocationDroitEntree || isCessionDroitAuBail;
  // Une licence d'exploitation est un actif INCORPOREL : pas de surface, pas d'adresse précise
  const isLicence = data.objet === 'licence' || data.objet === 'licence_exploitation' 
    || (data.profil || '').toLowerCase().includes('licence')
    || (data.sous_profil || '').toLowerCase().includes('licence');
  // Parts sociales : on cède des parts d'une société, pas un local
  const isPartsSociales = data.operation === 'parts_sociales';
  // Pas de surface obligatoire pour ces 2 cas
  const surfaceRequired = !isLicence && !isPartsSociales;

  const searchAdresse = async (query) => {
    if (query.length < 3) {
      setAdresseSuggestions([]);
      return;
    }
    setLoadingAdresse(true);
    try {
      const response = await axios.get(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=10`
      );
      const adresses = response.data.features.map(feature => ({
        adresse: feature.properties.name,
        ville: feature.properties.city,
        codePostal: feature.properties.postcode,
        contexte: feature.properties.context,
        label: feature.properties.label
      }));
      setAdresseSuggestions(adresses);
    } catch (error) {
      console.error('Erreur recherche adresse:', error);
    } finally {
      setLoadingAdresse(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchAdresse(adresseSearch), 300);
    return () => clearTimeout(timer);
  }, [adresseSearch]);

  const selectAdresse = (adresseData) => {
    updateData({
      adresse: adresseData.adresse,
      ville: adresseData.ville,
      code_postal: adresseData.codePostal
    });
    setAdresseSearch('');
    setAdresseSuggestions([]);
  };

  const generateDescription = async () => {
    if (!data.operation || !data.ville) {
      alert('Veuillez renseigner au moins l\'opération et la ville');
      return;
    }
    
    setGeneratingDesc(true);
    try {
      const response = await axios.post(`${API}/api/properties/generate-description`, {
        operation: data.operation,
        objet: data.objet,
        profil: data.profil,
        sous_profil: data.sous_profil,
        ville: data.ville,
        prix: data.prix,
        loyer: data.loyer,
        surface_totale_m2: data.surface_totale_m2
      });
      
      if (response.data && response.data.description) {
        updateData({ description_courte: response.data.description });
      }
    } catch (error) {
      console.error('Erreur génération description:', error);
      alert('Erreur lors de la génération. Vérifiez que l\'API est configurée.');
    } finally {
      setGeneratingDesc(false);
    }
  };

  const generateDetailedDescription = async () => {
    if (!data.operation || !data.ville) {
      alert('Veuillez renseigner au moins l\'opération et la ville');
      return;
    }
    setGeneratingDetailedDesc(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/api/properties/generate-detailed-description`,
        {
          operation: data.operation,
          objet: data.objet,
          profil: data.profil,
          sous_profil: data.sous_profil,
          ville: data.ville,
          prix: data.prix ? parseFloat(data.prix) : null,
          loyer: data.loyer ? parseFloat(data.loyer) : null,
          surface: data.surface_totale_m2 ? parseFloat(data.surface_totale_m2) : null,
          description_courte: data.description_courte || null,
          enseigne: data.enseigne || null,
          type: 'fiche_technique',
          language: 'Français',
        },
        { headers },
      );
      if (response.data?.content) {
        updateData({ description_detaillee: response.data.content });
      }
    } catch (error) {
      console.error('Erreur génération description détaillée:', error);
      alert("Erreur génération description détaillée : " + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingDetailedDesc(false);
    }
  };

  // Validation : ⛔ lot BI §7a — la MÊME règle que `champsManquantsInfosCles`, sans la
  // dupliquer. Une seule liste de champs manquants décide, ici comme au passage d'étape.
  const isComplete = champsManquants(4, data).length === 0;

  // Debug pour location_droit_entree
  if (isLocationDroitEntree) {
    console.log('[Step3_KeyInfo] Location droit entrée validation:', {
      adresse: !!data.adresse,
      ville: !!data.ville,
      code_postal: !!data.code_postal,
      surface_totale_m2: !!data.surface_totale_m2,
      droit_entree: data.droit_entree,
      loyer_mensuel: data.loyer_mensuel,
      isComplete
    });
  }

  return (
    <div className="flex flex-col h-full">
      <CompletionBar data={data} currentStep={3} />
      
      <div className="mb-3">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Informations Clés</h2>
        <p className="text-xs text-gray-600">Titre, adresse, prix/loyer et surface. <span className="text-red-500 font-bold">* Obligatoire</span></p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">
        {/* Titre de l'annonce */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-700">
              Titre de l'annonce
            </label>
            <button
              type="button"
              onClick={generateTitleAI}
              disabled={generatingTitle || !data.operation}
              className="text-xs font-semibold text-purple-600 hover:text-purple-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              title={data.operation ? "Générer un titre accrocheur avec l'IA" : "Sélectionnez d'abord le type de transaction"}
              data-testid="wizard-generate-title-ia"
            >
              <i className={`bi ${generatingTitle ? 'bi-arrow-clockwise animate-spin' : 'bi-stars'}`}></i>
              {generatingTitle ? 'Génération…' : 'Générer avec l\'IA'}
            </button>
          </div>
          <input
            type="text"
            value={data.titre_annonce || ''}
            onChange={(e) => updateData({ titre_annonce: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            placeholder="Ex: Local commercial 120m² - Emplacement n°1 Centre-ville"
          />
          <p className="text-xs text-gray-400 mt-0.5">Titre accrocheur pour les annonces (optionnel)</p>
        </div>

        {/* Adresse complète */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Adresse complète <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={adresseSearch || data.adresse}
            onChange={(e) => {
              setAdresseSearch(e.target.value);
              updateData({ adresse: e.target.value });
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            placeholder="12 rue de la République, 75001 Paris"
          />
          {adresseSuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto">
              {adresseSuggestions.map((adresse, idx) => (
                <button
                  key={idx}
                  onClick={() => selectAdresse(adresse)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-sm"
                >
                  <div className="font-medium">{adresse.label}</div>
                  <div className="text-xs text-gray-600">{adresse.contexte}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Ville + Code postal */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Ville <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.ville}
              readOnly
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
              placeholder="Sélectionnez une adresse"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Code postal <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.code_postal}
              readOnly
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
            />
          </div>
        </div>

        {/* Prix/Loyer/Droit d'entrée selon opération */}
        {deuxMontants ? (
          // CAS SPÉCIAL : 2 montants (+ Surface + Secteur = 4 colonnes)
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {isCessionDroitAuBail ? 'Prix du droit au bail (€)' : 'Pas-de-porte (€)'} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={(isCessionDroitAuBail ? data.prix : data.droit_entree) || ''}
                onChange={(e) => updateData(isCessionDroitAuBail
                  ? { prix: e.target.value }
                  : { droit_entree: e.target.value })}
                data-testid={isCessionDroitAuBail ? 'wizard-prix-droit-au-bail' : 'wizard-droit-entree'}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                placeholder="50000"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Loyer HT (€/mois) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={data.loyer_mensuel || ''}
                onChange={(e) => updateData({ loyer_mensuel: e.target.value })}
                data-testid="wizard-loyer-mensuel"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                placeholder="2500"
              />
            </div>
            {surfaceRequired && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Surface (m²) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={data.surface_totale_m2}
                  onChange={(e) => updateData({ surface_totale_m2: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="120"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Secteur</label>
              <input
                type="text"
                value={data.secteur}
                onChange={(e) => updateData({ secteur: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                placeholder="Centre-ville"
              />
            </div>
          </div>
        ) : (
          // CAS NORMAL : Prix OU Loyer + Surface (sauf licences) + Secteur
          <div className={surfaceRequired ? 'grid grid-cols-1 sm:grid-cols-3 gap-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {isPrixOperation ? 'Prix net vendeur (€)' : getPriceLabel(data.operation)} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={isPrixOperation ? data.prix : data.loyer}
                onChange={(e) => updateData(isPrixOperation ? { prix: e.target.value } : { loyer: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                placeholder="150000"
              />
            </div>
            {surfaceRequired && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Surface (m²) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={data.surface_totale_m2}
                  onChange={(e) => updateData({ surface_totale_m2: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="120"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Secteur</label>
              <input
                type="text"
                value={data.secteur}
                onChange={(e) => updateData({ secteur: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                placeholder="Centre-ville"
              />
            </div>
          </div>
        )}

        {/* Charges (si location classique ou location_droit_entree) */}
        {!isPrixOperation && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Charges mensuelles (€)</label>
            <input
              type="number"
              value={data.charges}
              onChange={(e) => updateData({ charges: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              placeholder="150"
            />
          </div>
        )}

        {/* Honoraires d'agence — iter124 : UN SEUL champ + sélecteur d'unité (€ TTC / %).
            La base du taux est le PRIX NET VENDEUR (vente) ; le PRIX FAI est calculé. */}
        {(() => {
          const unit = data.honoraires_unite || (data.honoraires_pct ? 'pct' : 'ttc');
          const value = unit === 'pct' ? (data.honoraires_pct ?? '') : (data.honoraires ?? '');
          const net = parseFloat(isPrixOperation ? data.prix : (data.droit_entree || data.loyer)) || null;
          const ttc = unit === 'pct'
            ? (net && parseFloat(data.honoraires_pct) ? Math.round(net * parseFloat(data.honoraires_pct)) / 100 : null)
            : (parseFloat(data.honoraires) || null);
          const fai = (isPrixOperation && net != null && ttc != null) ? net + ttc : null;
          const setUnit = (u) => updateData({ honoraires_unite: u, honoraires: null, honoraires_pct: null });
          return (
            <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3">
              <label className="block text-xs font-bold text-blue-800 mb-2 uppercase tracking-wide">
                <i className="bi bi-receipt mr-1"></i> Honoraires d'agence
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                    Honoraires {unit === 'pct' ? (isPrixOperation ? '(% du prix net vendeur)' : '(% du loyer annuel HT)') : '(€ TTC)'}
                  </label>
                  <div className="flex items-stretch gap-2">
                    <input
                      type="number"
                      step={unit === 'pct' ? '0.01' : '1'}
                      value={value}
                      onChange={(e) => updateData(unit === 'pct'
                        ? { honoraires_pct: e.target.value, honoraires: null }
                        : { honoraires: e.target.value, honoraires_pct: null })}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 tabular-nums"
                      placeholder={unit === 'pct' ? '10' : '18000'}
                      data-testid="wizard-honoraires-valeur"
                    />
                    <div className="shrink-0 inline-flex rounded border border-blue-300 bg-blue-50/70 p-0.5" role="group" data-testid="wizard-honoraires-unite">
                      {[{ v: 'ttc', l: '€ TTC' }, { v: 'pct', l: '%' }].map(u => (
                        <button
                          key={u.v}
                          type="button"
                          data-testid={`wizard-honoraires-unite-${u.v}`}
                          aria-pressed={unit === u.v}
                          onClick={() => unit !== u.v && setUnit(u.v)}
                          className={`px-2.5 rounded-sm text-xs font-bold transition-colors duration-150 ${
                            unit === u.v ? 'bg-blue-600 text-white shadow-sm' : 'text-blue-700 hover:bg-blue-100'
                          }`}
                        >
                          {u.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 mb-1">À la charge de</label>
                  <select
                    value={data.honoraires_charge || ''}
                    onChange={(e) => updateData({ honoraires_charge: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 bg-white"
                    data-testid="wizard-honoraires-charge"
                  >
                    <option value="">— Choisir —</option>
                    <option value="vendeur">{isPrixOperation ? 'Vendeur' : 'Bailleur'}</option>
                    <option value="acquereur">{isPrixOperation ? 'Acquéreur' : 'Preneur'}</option>
                  </select>
                </div>
              </div>
              {isPrixOperation && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
                  <span>Honoraires TTC : <span className="font-semibold tabular-nums" data-testid="wizard-honoraires-ttc-calcule">{ttc != null ? `${ttc.toLocaleString('fr-FR')} €` : '—'}</span></span>
                  <span>Prix FAI (calculé) : <span className="font-bold text-slate-900 tabular-nums" data-testid="wizard-prix-fai-calcule">{fai != null ? `${fai.toLocaleString('fr-FR')} €` : '—'}</span></span>
                </div>
              )}
              <p className="text-[10px] text-gray-500 mt-1.5">
                <i className="bi bi-info-circle mr-1"></i>
                {isPrixOperation
                  ? 'Le prix saisi ci-dessus est le PRIX NET VENDEUR. Le prix FAI = net vendeur + honoraires TTC (jamais saisi).'
                  : 'Commission due une seule fois à la signature du bail (base : loyer annuel HT).'}
              </p>
            </div>
          );
        })()}

        {/* Description courte */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Description courte (IA)</label>
          <div className="relative">
            <textarea
              value={data.description_courte}
              onChange={(e) => updateData({ description_courte: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 h-16"
              placeholder="Accroche marketing en 2 phrases..."
            />
            <button
              onClick={generateDescription}
              disabled={generatingDesc}
              className="absolute bottom-2 right-2 text-xs bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-1.5 rounded-md font-semibold shadow-sm hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 transition inline-flex items-center gap-1.5"
              data-testid="wizard-generate-short-description-ia"
            >
              <i className={`bi ${generatingDesc ? 'bi-arrow-repeat animate-spin' : 'bi-stars'}`}></i>
              {generatingDesc ? 'Génération...' : 'Générer avec l\'IA'}
            </button>
          </div>
        </div>

        {/* Description détaillée (fiche technique) */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Description détaillée (IA — fiche technique)</label>
          <div className="relative">
            <textarea
              value={data.description_detaillee || ''}
              onChange={(e) => updateData({ description_detaillee: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 h-32"
              placeholder="Description complète du bien (4-8 phrases : situation, surfaces, équipements, atouts, opportunité)..."
              data-testid="wizard-description-detaillee"
            />
            <button
              type="button"
              onClick={generateDetailedDescription}
              disabled={generatingDetailedDesc || !data.operation || !data.ville}
              title={(!data.operation || !data.ville) ? "Renseignez d'abord l'opération et la ville" : "Générer une description détaillée avec l'IA"}
              className="absolute bottom-2 right-2 text-xs bg-gradient-to-r from-violet-600 to-purple-600 text-white px-3 py-1.5 rounded-md font-semibold shadow-sm hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-1.5"
              data-testid="wizard-generate-detailed-description-ia"
            >
              <i className={`bi ${generatingDetailedDesc ? 'bi-arrow-repeat animate-spin' : 'bi-stars'}`}></i>
              {generatingDetailedDesc ? 'Génération...' : 'Générer avec l\'IA'}
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Stocké comme <span className="font-mono">descriptif.fiche_technique · Français</span>. Tu pourras ajouter d'autres langues / types après création.
          </p>
        </div>
      </div>

      {/* lot Z2 — ne nomme que les champs RÉELLEMENT manquants (avant : rappel statique
          de tous les champs obligatoires, qui n'aidait pas à trouver l'oubli). */}
      <BarreEtape
        manquants={<ChampsManquants champs={champsManquants(4, data)} testId="wizard-step3-champs-manquants" />}
      >
        <button
          onClick={prevStep}
          className="h-11 md:h-auto px-5 md:py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded transition-colors"
        >
          Retour
        </button>
        <button
          onClick={nextStep}
          disabled={!isComplete}
          className="h-12 md:h-auto px-5 md:py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded transition-colors"
        >
          Suivant
        </button>
      </BarreEtape>
    </div>
  );
};

export default Step3_KeyInfo;